import { collection, addDoc, getDocs, query, where, serverTimestamp, doc, updateDoc, getDoc, increment } from 'firebase/firestore';
import { firestore } from '../firebaseConfig';
import fnc from '../../mymod/commFunction';

/**
 * 1:1 직접 메시지를 전송하는 함수
 * @param {string} chatRoomName - 채팅방 이름
 * @param {string} senderId - 발신자 ID
 * @param {string} senderName - 발신자 이름
 * @param {string} receiverId - 수신자 ID
 * @param {string} messageText - 메시지 내용
 * @param {string} messageType - 메시지 타입 ('text' 또는 'image')
 * @param {array} imageData - 이미지 데이터 (이미지 메시지인 경우)
 * @returns {Promise<object>} 전송 결과
 * 
 * 특징:
 * - roomKey 기반으로 1:1 채팅방 관리
 * - participants 배열 자동 정규화 (중복 제거)
 * - senderId와 receiverId가 같아도 안전하게 처리
 * - 읽지 않은 메시지 수 자동 업데이트
 */
export const sendDirectMessage = async (chatRoomName, senderId, senderName, receiverId, messageText, messageType = 'text', imageData = null, options = {}) => {
    try {
        // 입력 값 검증
        if (!senderId || !receiverId || !messageText) {
            throw new Error('필수 매개변수가 누락되었습니다.');
        }
        
        // 1. 두 사용자 간의 채팅방이 있는지 확인/생성
        let chatRoomId = await findOrCreateChatRoom(chatRoomName, senderId, senderName, receiverId, options);
        
        // 2. 메시지 데이터 구성
        const messageData = {
            senderId: senderId,
            senderName: senderName,
            text: messageText,  // ⚠️ 'message' → 'text' 변경
            messageType: messageType,
            timestamp: serverTimestamp(),  // ⚠️ Date.now() → serverTimestamp()
            chatroomId: chatRoomId
        };

        // 이미지 데이터 추가 (이미지 메시지인 경우)
        if (messageType === 'image' && imageData) {
            messageData.images = imageData;
        }

        // 3. Firestore 서브컬렉션에 메시지 저장
        const messagesRef = collection(firestore, `chatRooms/${chatRoomId}/messages`);
        const messageDoc = await addDoc(messagesRef, messageData);
        console.log('✅ [DirectMessage] 메시지 저장 완료:', messageDoc.id);
        
        // 4. Firestore 채팅방 정보 업데이트 (읽지 않은 메시지 수 포함)
        try {
            const chatroomRef = doc(firestore, 'chatRooms', chatRoomId);
            const chatroomDoc = await getDoc(chatroomRef);
            
            if (chatroomDoc.exists()) {
                const chatroomData = chatroomDoc.data();
                
                // 채팅방 정보 업데이트 데이터 구성
                const updateData = {
                    lastMessage: messageText,
                    lastMessageTime: serverTimestamp(),
                    [`lastReadTimestamp.${senderId}`]: Date.now()  // 발신자는 읽은 것으로 처리
                };

                // 수신자의 읽지 않은 메시지 수 증가
                if (chatroomData.participants) {
                    chatroomData.participants.forEach(participantId => {
                        if (participantId !== senderId) {
                            updateData[`unreadCount.${participantId}`] = increment(1);  // ⚠️ increment 사용
                        }
                    });
                }

                await updateDoc(chatroomRef, updateData);
                console.log('✅ [DirectMessage] 채팅방 정보 업데이트 완료');
            }
        } catch (updateError) {
            console.error('❌ [DirectMessage] 채팅방 정보 업데이트 오류:', updateError);
            // 메시지 전송은 성공했으므로 에러를 throw하지 않음
        }
        
        return {
            success: true,
            messageId: messageDoc.id,
            chatRoomId: chatRoomId
        };
    } catch (error) {
        console.error('메시지 전송 실패:', error);
        return {
            success: false,
            error: error.message
        };
    }
};

/**
 * 두 사용자 간의 1:1 채팅방을 찾거나 생성
 * @param {string} chatRoomName - 채팅방 이름
 * @param {string} senderId - 발신자 ID
 * @param {string} senderName - 발신자 이름
 * @param {string} receiverId - 수신자 ID
 * @returns {Promise<string>} 채팅방 ID
 */
const findOrCreateChatRoom = async (chatRoomName, senderId, senderName, receiverId, options = {}) => {
    try {
        // 1:1 채팅을 위한 roomKey 생성 (사용자 ID 정렬로 일관성 보장)
        const participantKey = [senderId, receiverId].sort().join('_');
        const contextKey = options?.contextKey ? String(options.contextKey) : '';
        const roomKey = options?.roomKey || (contextKey ? `${participantKey}_${contextKey}` : participantKey);
        
        // 참여자 목록 정규화 (중복 제거)
        const participants = Array.from(new Set([senderId, receiverId]));
        
        // roomKey로 기존 채팅방 찾기
        const q = query(
            collection(firestore, 'chatRooms'),
            where('roomKey', '==', roomKey)
        );
        
        const querySnapshot = await getDocs(q);
        
        // 기존 채팅방이 있으면 반환
        if (!querySnapshot.empty) {
            const existingRoom = querySnapshot.docs[0];
            return existingRoom.id;
        }

        // 없으면 새로 생성
        const chatroomData = {
            type: "direct", // direct = 1:1, group = 다중
            roomKey: roomKey, // 1:1 채팅 식별자
            name: options?.roomName || chatRoomName,
            participants: participants,
            createdBy: senderId,
            createdByName: senderName,
            targetRole: options?.targetRole || 'student',
            contextType: options?.contextType || '',
            contextId: options?.contextId || '',
            studentId: options?.studentId || '',
            studentName: options?.studentName || '',
            createdAt: serverTimestamp(),
            lastMessage: '',
            lastMessageTime: serverTimestamp(),
            // 모든 참여자의 읽지 않은 메시지 수 초기화
            unreadCount: participants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {}),
            // 모든 참여자의 마지막 읽은 시간 초기화
            lastReadTimestamp: participants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {})
        };

        const docRef = await addDoc(collection(firestore, 'chatRooms'), chatroomData);
        
        return docRef.id;
    } catch (error) {
        console.error('채팅방 찾기/생성 실패:', error);
        throw error;
    }
};

/**
 * 그룹 채팅방 생성 함수 (향후 사용)
 * @param {string} title - 그룹 채팅방 제목
 * @param {array} participants - 참여자 ID 배열
 * @param {string} createdBy - 생성자 ID
 * @param {string} createdByName - 생성자 이름
 * @returns {Promise<string>} 생성된 채팅방 ID
 */
export const createGroupChatRoom = async (title, participants, createdBy, createdByName) => {
    try {
        // participants 정규화 (중복 제거)
        const normalizedParticipants = Array.from(new Set(participants));
        
        if (normalizedParticipants.length < 2) {
            throw new Error('그룹 채팅방은 최소 2명 이상이어야 합니다.');
        }

        const chatroomData = {
            type: "group", // group = 다중 채팅
            title: title,
            participants: normalizedParticipants,
            createdBy: createdBy,
            createdByName: createdByName,
            createdAt: serverTimestamp(),
            lastMessage: '',
            lastMessageTime: serverTimestamp(),
            unreadCount: normalizedParticipants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {}),
            lastReadTimestamp: normalizedParticipants.reduce((acc, userId) => {
                acc[userId] = 0;
                return acc;
            }, {})
        };

        const docRef = await addDoc(collection(firestore, 'chatRooms'), chatroomData);
        console.log('새 그룹 채팅방 생성:', {
            id: docRef.id,
            title: title,
            participants: normalizedParticipants
        });
        
        return docRef.id;
    } catch (error) {
        console.error('그룹 채팅방 생성 실패:', error);
        throw error;
    }
};

/**
 * mssql DB에서 저장된 chatRoomId 조회 (수신자 기준)
 */
const getChatRoomIdFromDatabase = async (senderId, receiverId) => {
    try {
        return new Promise((resolve) => {
            fnc.sendAxios('/socialLoginRoutes/select/chatRoomId', {
                receiverId
            }, (result) => {
                if (result && result.length > 0 && result[0].chatRoomId) {
                    console.log('DB에서 조회한 chatRoomId:', result[0].chatRoomId);
                    resolve(result[0].chatRoomId);
                } else {
                    resolve(null);
                }
            });
        });
    } catch (error) {
        console.error('chatRoomId 조회 중 오류:', error);
        return null;
    }
};

/**
 * chatRoomId를 MSSQL socialLogin 테이블에 저장 (수신자만)
 */
const saveChatRoomIdToDatabase = async (senderId, receiverId, chatRoomId) => {
    try {
        await new Promise((resolve) => {
            fnc.sendAxios('/socialLoginRoutes/update/chatRoomId', {
                senderId,
                receiverId,
                chatRoomId
            }, (result) => {
                if (!result.success) {
                    console.error('chatRoomId 저장 실패:', result.error);
                } else {
                    console.log('chatRoomId 저장 완료 (수신자:', receiverId, ')');
                }
                resolve(result);
            });
        });
    } catch (error) {
        console.error('chatRoomId 저장 중 오류:', error);
    }
};



